import type { CheckpointDef } from '../data/types';
import { es } from '../i18n/es';

/** Runtime state of a checkpoint: its flare turns from red to green when activated (GDD §6.2). */
export interface CheckpointState {
  readonly id: string;
  readonly x: number;
  active: boolean;
  flare: 'red' | 'green';
}

export interface CheckpointTrack {
  readonly checkpoints: CheckpointState[];
  /** Index of the furthest activated checkpoint, -1 before the first one. */
  activeIndex: number;
}

export function createCheckpointTrack(checkpoints: readonly CheckpointDef[]): CheckpointTrack {
  return {
    checkpoints: [...checkpoints]
      .sort((a, b) => a.x - b.x)
      .map((checkpoint) => ({ id: checkpoint.id, x: checkpoint.x, active: false, flare: 'red' })),
    activeIndex: -1,
  };
}

export function activeCheckpointId(track: CheckpointTrack): string | null {
  return track.checkpoints[track.activeIndex]?.id ?? null;
}

export const CHECKPOINT_RADIO_LINE = es['voice.radio.checkpoint'];

/**
 * Phase 4 (GDD §6.2, rules 1 to 3): the trigger spans the whole screen height, so a line is crossed when
 * the plane centre reaches its x. Of the lines crossed, only the furthest one beyond the active
 * checkpoint is activated (an earlier one never replaces a later one); lines left behind without
 * activation are marked so they never fire later. An active checkpoint never fires again.
 * Returns the checkpoint activated on this tick, if any.
 */
export function crossCheckpoints(track: CheckpointTrack, planeX: number): CheckpointState | null {
  let furthest = -1;
  track.checkpoints.forEach((checkpoint, index) => {
    if (planeX >= checkpoint.x) furthest = index;
  });
  if (furthest <= track.activeIndex) return null;
  for (let index = 0; index < furthest; index++) track.checkpoints[index]!.active = true;
  const checkpoint = track.checkpoints[furthest]!;
  checkpoint.active = true;
  checkpoint.flare = 'green';
  track.activeIndex = furthest;
  return checkpoint;
}
